import React, { useMemo } from 'react';

import { SettingsIcon, InfoCircleIcon, InfoCircleFilledIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import { Control, Divider, Tooltip } from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';

import st from './BasicSettingsSubForm.module.sass';
import { DefaultCaption } from './DropzoneCaption.tsx';

const modalContent = (
  <div className={st.Hint}>
    <div className={st.Hint__Title}>
      <InfoCircleFilledIcon />
      <h5><Trans i18nKey="checks:video check form.liveness.tooltip.title" /></h5>
    </div>
    <div>
      <div className={st.Hint__Body}>
        <Trans i18nKey="checks:video check form.liveness.tooltip.preview" />
      </div>
    </div>
  </div>
);

export function BasicSettingsSubForm({ onOpenSettings, onCancel }) {
  const form = useFormContext();
  const { t } = useTranslation();
  const caption = useMemo(() => <DefaultCaption />, []);

  const fileList = useWatch({ control: form.control, name: 'fileList' });
  const [liveness, deepfake, face, body] = useWatch({
    control: form.control,
    name: ['liveness', 'deepfake', 'face', 'body'],
  });
  const hasAnyCheck = Boolean(liveness || deepfake || face || body);

  const canSubmit = !!fileList && hasAnyCheck && !form.formState.isSubmitting;

  return (
    <React.Fragment>
      <h5 className={st.PageTitle}>{t('checks:video check form.title')}</h5>
      <Control.FileDropzone
        caption={caption}
        data-testid="fileDropzone"
        id="fileList"
        {...form.register('fileList', {
          required: validate.required(),
        })}
        defaultFile={fileList}
        errors={form.formState.errors}
        onlyVideos
      />
      <div className={st.SettingsSubtitle}>
        <div>{t('checks:video check form.select checks')}</div>
        <Control.Button
          icon={<SettingsIcon />}
          kind="secondary"
          onClick={onOpenSettings}
          size="small"
          variant="flat"
        >
          {t('checks:video check form.settings btn')}
        </Control.Button>
      </div>

      <div className={st.Settings}>
        <div className={st.SettingsRow}>
          <Control.Checkbox
            {...form.register('liveness')}
            data-testid="liveness"
            label={(
              <Trans
                components={{ highlight: <span className={st.Hint__Color} /> }}
                i18nKey="checks:video check form.liveness.label"
              />
            )}
          />
          <Tooltip
            className={st.Tooltip}
            modalContent={modalContent}
            tooltipContent={<Trans i18nKey="checks:video check form.liveness.tooltip.preview" />}
            tooltipProps={{ effect: 'solid' }}
          >
            <InfoCircleIcon className="StaticIcon" />
          </Tooltip>
        </div>

        <Control.Checkbox
          {...form.register('deepfake')}
          data-testid="deepfake"
          label={(
            <Trans
              components={{ highlight: <span className={st.Hint__Color} /> }}
              i18nKey="checks:video check form.deepfake.label"
            />
          )}
        />
        <Control.Checkbox
          {...form.register('face')}
          data-testid="face"
          label={(
            <Trans
              components={{ highlight: <span className={st.Hint__Color} /> }}
              i18nKey="checks:video check form.face atributes.label"
            />
          )}
        />
        <Control.Checkbox
          {...form.register('body')}
          data-testid="body"
          label={(
            <Trans
              components={{ highlight: <span className={st.Hint__Color} /> }}
              i18nKey="checks:video check form.body atributes.label"
            />
          )}
        />
      </div>

      <Divider />

      <div className={st.Action}>
        <Control.Button
          data-testid="submit btn"
          disabled={!canSubmit}
          fullWidth
          type="submit"
        >
          {t('checks:video check form.submit btn')}
        </Control.Button>
        <Control.Button
          data-testid="cancel btn"
          fullWidth
          kind="secondary"
          onClick={onCancel}
          variant="outlined"
        >
          {t('shared:btn.cancel')}
        </Control.Button>
      </div>
    </React.Fragment>
  );
}

BasicSettingsSubForm.propTypes = {
  onOpenSettings: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
